import { DIVIDED, INSET, MUTED, PRIMARY, ROW, Flow, Panel, Photo, Pill, type Tone } from './SceneKit';
import { SAMPLE_FIGURES as F, usd } from '../../data/sample-figures';

/*
 * Illustrative scenes for the guides, embedded in a guide's markdown with `![](scene:<name>)`.
 * Like the feature-page scenes they are built from markup with plausible sample data and only use
 * states and terms the app really has, but each one shows the moment a single guide step is about.
 */

function ListingScene(): JSX.Element {
  const sites = ['Craigslist', 'Facebook Marketplace'];
  return (
    <div>
      <Panel title="Unit · Maple Ave, Unit A" meta="Photos and description saved to the unit">
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <Photo key={i} className="aspect-[4/3]" />
          ))}
        </div>
        <div className={`mt-3 text-sm ${PRIMARY}`}>Sunny 3-bed near the park</div>
        <div className={MUTED}>{usd(F.listingRent)} · Available Nov 1 · Pets OK · Tenant pays electric</div>
      </Panel>
      <Flow label="Fill each post from the same details" />
      <Panel title="Marketing · Maple Ave, Unit A">
        <ul className={DIVIDED}>
          {sites.map((site) => (
            <li key={site} className={`${ROW} items-center`}>
              <span className={PRIMARY}>{site}</span>
              <span className="flex flex-shrink-0 items-center gap-1.5">
                <Pill tone="good">Filled in</Pill>
                <Pill tone="info">You press Post</Pill>
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function InboxScene(): JSX.Element {
  const threads: { name: string; preview: string; tone: Tone; status: string }[] = [
    { name: 'Priya R.', preview: 'Is it still available? Are dogs OK?', tone: 'good', status: 'Assistant replied' },
    { name: 'Marcus B.', preview: 'Can I move in two weeks early?', tone: 'warn', status: 'Needs you' },
    { name: 'Elena V.', preview: 'Does the rent include parking?', tone: 'info', status: 'Reply waiting for approval' },
  ];
  return (
    <Panel title="Inbox · Maple Ave, Unit A" meta="One address for every inquiry">
      <ul className={DIVIDED}>
        {threads.map((t) => (
          <li key={t.name} className={ROW}>
            <div className="min-w-0">
              <div className={PRIMARY}>{t.name}</div>
              <div className={`truncate ${MUTED}`}>{t.preview}</div>
            </div>
            <Pill tone={t.tone}>{t.status}</Pill>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ShowingScene(): JSX.Element {
  const slots = [
    { time: '10:00 AM', taken: true },
    { time: '10:30 AM', taken: false },
    { time: '11:00 AM', taken: false },
    { time: '1:00 PM', taken: false },
  ];
  return (
    <Panel title="Book a showing · Maple Ave, Unit A" meta="Saturday, Nov 8">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {slots.map((s) => (
          <div
            key={s.time}
            className={`px-2 py-2 text-center text-sm ${s.taken ? 'text-[var(--landing-text-muted)] line-through' : PRIMARY} ${INSET} ${
              s.time === '10:30 AM' ? 'border-[var(--landing-accent)]' : ''
            }`}
          >
            {s.time}
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Pill tone="good">10:30 AM confirmed</Pill>
        <span className={MUTED}>Confirmation emailed to the prospect</span>
      </div>
    </Panel>
  );
}

function PipelineScene(): JSX.Element {
  const columns: { stage: string; people: { name: string; source: string }[] }[] = [
    { stage: 'Inquiring', people: [{ name: 'Priya R.', source: 'Facebook' }, { name: 'Marcus B.', source: 'Craigslist' }] },
    { stage: 'Showing', people: [{ name: 'Elena V.', source: 'Craigslist' }] },
    { stage: 'Applying', people: [{ name: 'Tom O.', source: 'Facebook' }] },
    { stage: 'Verifying', people: [{ name: 'Hannah L.', source: 'Referral' }] },
    { stage: 'Deciding', people: [{ name: 'Jordan W.', source: 'Craigslist' }] },
  ];
  return (
    <Panel title="Leads · Maple Ave, Unit A" meta="6 active">
      <div className="overflow-x-auto">
        <div className="grid min-w-[34rem] grid-cols-5 gap-2">
          {columns.map((c) => (
            <div key={c.stage} className={`p-2 ${INSET}`}>
              <div className={`mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide ${MUTED}`}>
                {c.stage}
                <span>{c.people.length}</span>
              </div>
              <div className="space-y-2">
                {c.people.map((p) => (
                  <div key={p.name} className="rounded-md border border-[var(--landing-border)] bg-[var(--landing-surface)] px-2 py-1.5">
                    <div className={`text-xs ${PRIMARY}`}>{p.name}</div>
                    <div className="text-[10px] text-[var(--landing-text-muted)]">{p.source}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

function DecisionScene(): JSX.Element {
  const checks = ['Income verified', 'Employment verified', 'Prior landlord called', 'Credit report on file'];
  return (
    <div>
      <Panel title="Applicant · Tom O." meta="Screening">
        <ul className="grid gap-1.5 text-sm sm:grid-cols-2">
          {checks.map((c) => (
            <li key={c} className="flex items-center gap-2 text-[var(--landing-text-secondary)]">
              <span className="text-emerald-400">&#10003;</span>
              {c}
            </li>
          ))}
        </ul>
      </Panel>
      <Flow label="Not selected: the notice is drafted for you" />
      <Panel title="Rejection notice · draft" meta="To: Tom O.">
        <div className={`space-y-1.5 p-3 text-xs text-[var(--landing-text-secondary)] ${INSET}`}>
          <div>Thank you for applying. We have chosen not to move forward with your application.</div>
          <div className={MUTED}>Reason: based on a consumer report. Includes the reporting company&apos;s details and your right to a free copy.</div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Pill tone="info">Review and send</Pill>
        </div>
      </Panel>
    </div>
  );
}

function LeaseFillScene(): JSX.Element {
  const fields = [
    ['Tenant', 'Priya Patel'],
    ['Co-signer', 'Marcus Patel'],
    ['Move-in', 'Nov 1'],
    ['Rent', usd(F.listingRent)],
    ['Pets', '1 cat'],
  ];
  const guidelines = ['Security deposit within your state’s limit', 'Required disclosures included', 'Lead-based paint (built before 1978)'];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Panel title="New lease · Maple Ave, Unit A" meta="Pre-filled from the application">
        <dl className={`${DIVIDED} text-sm`}>
          {fields.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 py-1.5">
              <dt className={MUTED}>{k}</dt>
              <dd className={PRIMARY}>{v}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel title="Your state’s guidelines">
        <ul className="space-y-2 text-sm">
          {guidelines.map((g) => (
            <li key={g} className="flex items-start gap-2 text-[var(--landing-text-secondary)]">
              <span className="text-emerald-400">&#10003;</span>
              {g}
            </li>
          ))}
        </ul>
        <div className={`mt-3 ${MUTED}`}>Reminders, not legal advice</div>
      </Panel>
    </div>
  );
}

function SignatureScene(): JSX.Element {
  const signers: { name: string; role: string; tone: Tone; status: string }[] = [
    { name: 'Priya Patel', role: 'Tenant', tone: 'good', status: 'Signed ✓' },
    { name: 'Marcus Patel', role: 'Co-signer', tone: 'info', status: 'Viewed' },
    { name: 'You', role: 'Landlord', tone: 'neutral', status: 'Your turn' },
  ];
  return (
    <Panel title="Lease · Maple Ave, Unit A" meta="Awaiting signatures">
      <ul className={DIVIDED}>
        {signers.map((s) => (
          <li key={s.name} className={`${ROW} items-center`}>
            <div>
              <div className={PRIMARY}>{s.name}</div>
              <div className={MUTED}>{s.role}</div>
            </div>
            <Pill tone={s.tone}>{s.status}</Pill>
          </li>
        ))}
      </ul>
      <div className={`mt-3 flex flex-wrap justify-between gap-2 ${MUTED}`}>
        <span>Signs from any phone, no account</span>
        <span>Free, no per-document fee</span>
      </div>
    </Panel>
  );
}

function MoveInScene(): JSX.Element {
  const items: { name: string; tone: Tone; status: string }[] = [
    { name: 'Walls and paint', tone: 'warn', status: 'Flagged · 2 photos' },
    { name: 'Floors', tone: 'good', status: 'OK' },
    { name: 'Refrigerator', tone: 'good', status: 'OK' },
    { name: 'Smoke detector', tone: 'good', status: 'OK' },
  ];
  return (
    <Panel title="Move-in walkthrough · Living room" meta="Room 2 of 6">
      <ul className={DIVIDED}>
        {items.map((i) => (
          <li key={i.name} className={`${ROW} items-center`}>
            <span className="text-[var(--landing-text-secondary)]">{i.name}</span>
            <Pill tone={i.tone}>{i.status}</Pill>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-2">
        <Photo className="h-10 w-14 flex-shrink-0" />
        <Photo className="h-10 w-14 flex-shrink-0" />
        <span className={MUTED}>Tenant walkthrough link sent</span>
      </div>
    </Panel>
  );
}

function UnitFileScene(): JSX.Element {
  const docs: { name: string; note: string }[] = [
    { name: 'Listing', note: 'Photos and description' },
    { name: 'Application', note: 'Priya Patel' },
    { name: 'Signed lease', note: 'Nov 1, 12 months' },
    { name: 'Lead-based paint disclosure', note: 'Signed' },
    { name: 'Move-in walkthrough', note: '24 photos' },
  ];
  return (
    <Panel title="Maple Ave, Unit A · Documents" meta="Everything for this tenancy">
      <ul className={DIVIDED}>
        {docs.map((d) => (
          <li key={d.name} className={`${ROW} items-center`}>
            <span className={PRIMARY}>{d.name}</span>
            <span className={MUTED}>{d.note}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

const SCENES: Record<string, { label: string; Scene: () => JSX.Element }> = {
  listing: {
    label: 'Illustration: a unit with its photos and description, filling in Craigslist and Facebook Marketplace posts that the landlord reviews and posts.',
    Scene: ListingScene,
  },
  inbox: {
    label: 'Illustration: one inbox of prospect messages, one answered by the AI assistant, one needing the landlord and one reply waiting for approval.',
    Scene: InboxScene,
  },
  showing: {
    label: 'Illustration: a prospect picking a Saturday showing time from open slots and getting a confirmation.',
    Scene: ShowingScene,
  },
  pipeline: {
    label: 'Illustration: a board of leads moving from inquiring through showing, applying, verifying and deciding.',
    Scene: PipelineScene,
  },
  decision: {
    label: 'Illustration: an applicant with completed screening checks, and a drafted rejection notice ready to review and send.',
    Scene: DecisionScene,
  },
  'lease-fill': {
    label: 'Illustration: a lease pre-filled from the application beside a checklist of the state guidelines it includes.',
    Scene: LeaseFillScene,
  },
  signature: {
    label: 'Illustration: a lease with the tenant signed, the co-signer having viewed it and the landlord up next.',
    Scene: SignatureScene,
  },
  'move-in': {
    label: 'Illustration: a room-by-room move-in checklist with one flagged item and photos, and a link sent to the tenant.',
    Scene: MoveInScene,
  },
  'unit-file': {
    label: 'Illustration: a unit’s documents folder holding the listing, application, signed lease, disclosure and move-in photos.',
    Scene: UnitFileScene,
  },
};

/** Whether a guide scene exists (a guide embeds one with `![](scene:<name>)`). */
export function hasGuideScene(name: string): boolean {
  return name in SCENES;
}

/** The illustrative scene a guide embeds, or nothing if the name is unknown. */
export function GuideScene({ name }: { name: string }): JSX.Element | null {
  const scene = SCENES[name];
  if (!scene) return null;
  const { label, Scene } = scene;
  return (
    <div role="img" aria-label={label} data-testid={`guide-scene-${name}`} className="select-none">
      <Scene />
    </div>
  );
}
